<template>
  <ScreenBlocker>
    <FlotoContentLoader
      :loading="preferenceLoading || determinedLoginType !== true"
    >
      <template v-slot:waiting>
        <MRow class="flex-1 mb-2">
          <MCol class="flex flex-col flex-1 items-center justify-center">
            <MIcon
              name="spinner-third"
              class="fa-spin text-primary"
              size="3x"
            />
          </MCol>
        </MRow>
      </template>
      <FlotoThemeComponent portal="technician-portal">
        <FlotoShortcutHandler
          v-if="!preferenceLoading && determinedLoginType === true"
        >
          <SocketListener>
            <ChatProvider @chat-config="isChatEnabled = $event.enabled">
              <ChatSocketProvider socket-path="/chat-server">
                <PopupManagerProvider>
                  <MLayout class="mainLayout">
                    <FlotoAnchorLink
                      id="skip-to-content"
                      aria-label="Skip to main content"
                      class="skip-to-content-link"
                      @click="handleClickSkipToContent"
                    >
                      Skip to main content
                    </FlotoAnchorLink>
                    <Header :pinned="pinned" @change="handleChangePinnedMenu" />
                    <MLayout class="relative contentContainer">
                      <NavBar
                        :key="$route.fullPath"
                        :pinned="pinned"
                        @change="handleChangePinnedMenu"
                      />
                      <MLayout
                        class="mainLayoutContent"
                        :class="{ pinned: pinned }"
                      >
                        <FlotoLicenseChecker>
                          <SetupGuideProvider>
                            <FlotoScrollView>
                              <MLayoutContent
                                class="content-layout"
                                :class="{ 'pb-8': isChatEnabled }"
                              >
                                <slot />
                              </MLayoutContent>
                              <SetupHelpBubble />
                            </FlotoScrollView>
                          </SetupGuideProvider>
                        </FlotoLicenseChecker>
                      </MLayout>
                      <CollaborationContainer />
                    </MLayout>
                  </MLayout>
                </PopupManagerProvider>
              </ChatSocketProvider>
            </ChatProvider>
          </SocketListener>
        </FlotoShortcutHandler>
      </FlotoThemeComponent>
    </FlotoContentLoader>
  </ScreenBlocker>
</template>

<script>
import Bus from '@utils/emitter'
import dispatchActionForAllModules from '@utils/dispatch-action-for-all-modules'
import NavBar from '@components/layout/navbar'
import Header from '@components/layout/header'
import {
  PreferenceComputed,
  PreferenceMethods,
} from '@state/modules/preference'
import { authComputed, authMethods } from '@state/modules/auth'
import { BrandingComputed } from '@state/modules/branding'
import SocketListener from '@components/socket-listener.vue'
import ChatSocketProvider from '@components/chat-socket-provider.vue'
import PopupManagerProvider from '@components/providers/popup-manager-provider.vue'
import CollaborationContainer from '@components/chat/collaboration-container.vue'
import ChatProvider from '@components/chat/chat-provider.vue'
import { SsoConfigActions, SsoConfigComputed } from '@state/modules/sso-config'
import SetupGuideProvider from '@components/setup-guide/setup-guide-provider.vue'
import SetupHelpBubble from '@components/setup-guide/setup-help-bubble.vue'
import ScreenBlocker from '@components/screen-blocker.vue'
import { findFirstFocusableElement } from '@utils/focus'

export default {
  name: 'Layout',
  components: {
    NavBar,
    Header,
    SocketListener,
    CollaborationContainer,
    ChatProvider,
    ChatSocketProvider,
    SetupGuideProvider,
    PopupManagerProvider,
    SetupHelpBubble,
    ScreenBlocker,
  },
  data() {
    return {
      determinedLoginType: false,
      pinned: false,
      isChatEnabled: false,
      actionForAllModulesInitiated: false,
    }
  },
  computed: {
    ...PreferenceComputed,
    ...authComputed,
    ...BrandingComputed,
    ...SsoConfigComputed,
  },
  watch: {
    companyFavicon: {
      immediate: true,
      handler(newValue) {
        let link = document.querySelector("link[rel*='icon']")
        link.type = 'image/x-icon'
        link.rel = 'shortcut icon'
        link.href = newValue
        link = null
        // document.getElementsByTagName('head')[0].appendChild(link);
      },
    },
    $route(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleChangePinnedMenu(false)
      }
    },
  },
  created() {
    this.fetchSsoConfig()
    const logoutHandler = (user) => {
      if (user.loginSource === 'sso_login') {
        if (this.ssoConfig.idpLogoutUrl) {
          const connectorString =
            this.ssoConfig.idpLogoutUrl.indexOf('?') >= 0 ? '&' : '?'
          return (window.location.href = `${this.ssoConfig.idpLogoutUrl}${
            this.samlRequest
              ? `${connectorString}SAMLRequest=${encodeURIComponent(
                  this.samlRequest
                )}`
              : ''
          }`)
        } else if (this.ssoConfig.idpLoginUrl) {
          return (window.location.href = `${this.ssoConfig.idpLoginUrl}`)
        }
      }
      this.$router.replace(
        this.$modules.getModuleRoute('auth', 'login', {
          query: { redirectFrom: this.$route.fullPath },
        })
      )
    }

    Bus.$on('auth:logout', logoutHandler)

    this.$once('hook:beforeDestroy', () => {
      Bus.$off('auth:logout', logoutHandler)
    })

    const globallyLocaleLoadedHandler = () => {
      if (this.determinedLoginType && !this.actionForAllModulesInitiated) {
        this.actionForAllModulesInitiated = true
        dispatchActionForAllModules('init', {
          exclude: ['preference', 'auth', 'branding', 'license'],
        })
      }
    }
    Bus.$once('app:globallyLocaleLoaded', globallyLocaleLoadedHandler)
  },
  mounted() {
    if (this.isPortalLogin) {
      if (this.user.userType !== 'technician') {
        return this.$router.replace(
          this.$modules.getModuleRoute('support-portal')
        )
      }
    }
    if (this.user.userType !== 'technician') {
      return this.$router.replace(
        this.$modules.getModuleRoute('support-portal')
      )
    }
    this.changeLoginType('technician-portal')
    if (this.loggedIn) {
      this.fetchPreferences()
        .then(() => this.fetchTenantPrefrences())
        .then(() => {
          this.determinedLoginType = true
          // Automatically run the `init` action for every module,
          // if one exists.
          if (
            window.globallyLocaleLoaded &&
            !this.actionForAllModulesInitiated
          ) {
            this.actionForAllModulesInitiated = true
            dispatchActionForAllModules('init', {
              exclude: ['preference', 'auth', 'branding', 'license'],
            })
          }
        })
    }
  },
  methods: {
    ...PreferenceMethods,
    ...authMethods,
    ...SsoConfigActions,
    handleClickSkipToContent(event) {
      const mainContent = document.getElementById('main-content')
      if (mainContent) {
        event.stopPropagation()
        event.preventDefault()
        mainContent.focus()
      } else {
        const focusableElement = findFirstFocusableElement(
          this.$el.querySelector('.content-layout'),
          'breadcrumb'
        )

        if (focusableElement) {
          focusableElement.focus()
        }
      }
    },
    handleChangePinnedMenu($event) {
      requestAnimationFrame(() => {
        this.pinned = $event
      })
    },
  },
}
</script>

<style lang="less" scoped>
.mainLayout {
  height: 100vh;
}

.mainLayoutContent {
  padding: @layout-header-height 0 0 0;
  // background-color: var(--neutral-lightest) !important;
  &.pinned {
    padding-left: 50px;
  }
}

.contentContainer {
  min-height: calc(100vh - @layout-header-height);
}

.content-layout {
  min-height: calc(100vh - @layout-header-height);

  @apply overflow-x-hidden flex flex-col min-w-0; //pt-6 pl-3 pr-3
}

.skip-to-content-link {
  position: absolute;
  left: 200px;
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 8px;
  color: var(--default-button-text);
  background: var(--default-button-bg);
  border: 1px solid var(--default-button-border);
  border-radius: 15px;
  box-shadow: none;
  opacity: 0;
  transition: all 0.3s ease-in-out 0.2s;
  transform: translateY(-100%);
}

.skip-to-content-link:focus {
  z-index: 20;
  opacity: 1;
  transform: translateY(50%);
}
</style>
